import styled from '@emotion/styled';
import { ArrowLeftIcon } from '@seedlang/icons';
import { AppUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import InPlaceSelect from 'components/form/in_place_select';
import SearchInput from 'components/form/search_input';
import ReviewCardPanel from 'components/review/review_card_panel';
import ReviewCreateNote from 'components/review/review_create_note';
import { FieldLabel } from 'components/review/review_create_styles';
import VocabList from 'components/vocab/vocab_list';
import VocabWordInfoList from 'components/vocab/vocab_word_info_list';
import { observer } from 'mobx-react';
import React from 'react';

const Form = styled.div`
`;

const FormGroup = styled.div`
  margin: ${props => props.margin || '0 0 20px 0'};
`;

const WordSearchWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SearchResults = styled.div`
  margin-top: 10px;
`;

const BackButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  margin: 0 0 10px 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  .left-arrow-icon {
    cursor: pointer;
    height: 16px;
  }
  .left-arrow-icon svg {
    opacity: 0.8;
    fill: #333;
    height: 16px;
  }
`;

const ExistingReviewsWrapper = styled.div`
  margin: 0 0 8px 0;
  border-radius: 5px;
  padding: 10px;
  background: #f1f1f1;
`;

const ExistingReviewsTitle = styled.div`
  font-size: 13px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const ExistingReviewItem = styled.div`
  margin: 6px 0 12px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #dcdcdc;
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

@observer
class ReviewCreateWord extends React.Component {
  state = {
    expandedReviewCardIds: [],
  };

  reviewCardIsExpanded = (cardId) => {
    return this.state.expandedReviewCardIds.includes(cardId);
  }

  onToggleReviewCardExpanded = (cardId) => {
    if (this.reviewCardIsExpanded(cardId)) {
      this.setState({
        expandedReviewCardIds: this.state.expandedReviewCardIds.filter(id => id !== cardId),
      });
    } else {
      this.setState({
        expandedReviewCardIds: [...this.state.expandedReviewCardIds, cardId],
      });
    }
  }

  render() {
    const hasSearchValue = isPresent(this.props.wordSearchValue) && `${this.props.wordSearchValue}`.trim().length > 0;
    const shouldShowResults = !this.props.word && hasSearchValue;
    const existingReviewCount = this.props.reviewCardsForWord?.length || 0;
    const existingReviewsTitle = existingReviewCount > 1
      ? 'You already have reviews for this word.'
      : 'You already have a review for this word.';

    return (
      <Form>
        <FormGroup>
          {
            !this.props.word &&
            <>
              <WordSearchWrapper>
                <SearchInput
                  placeholder='Search for a word'
                  onChange={this.props.onUpdateWordSearchQuery}
                  insertText={this.props.onInsertWordSearchText}
                  focusOnId={this.props.wordSearchValue}
                  value={this.props.wordSearchValue}
                  background='#FFF'
                />
              </WordSearchWrapper>
              {
                shouldShowResults &&
                <SearchResults>
                  <VocabList
                    analyticsSource="review_create"
                    wordStore={AppUI.wordSearchStore}
                    showWordListSpinner={AppUI.wordSearchStore.showLoadingIcon}
                    showHeaders={Boolean(!AppUI.wordSearchStore.showLoadingIcon && AppUI.wordSearchStore.indexData.length > 0)}
                    searchQuery={this.props.wordSearchValue}
                    onUpdateWordSearchQuery={this.props.onUpdateWordSearchQuery}
                    setWord={this.props.onSetWord}
                    user={AppUI.user}
                    selectedWordIds={null}
                    onToggleAddWordToDeck={() => null}
                    onToggleLearned={() => null}
                    isMobile={false}
                    allowSelection={Boolean(AppUI.user?.isMember)}
                    showSource
                    afterSubmitSourceInput={() => null}
                    expandTranslation
                    selectedCardTypeId={null}
                    cardTypeId={null}
                    grammarSection={false}
                    showColumnCheckbox={false}
                    onToggleAddAllToDeck={() => null}
                    allToggleSelected={false}
                    onClickSort={() => null}
                    onToggleExpandTranslation={() => null}
                    onSelectWord={this.props.onSetWord}
                    reviewDeckOptions={null}
                    showNoResultsMessage={Boolean(
                      isPresent(this.props.wordSearchValue) &&
                      !AppUI.wordSearchStore.showLoadingIcon &&
                      AppUI.wordSearchStore.initialRequestReceived &&
                      AppUI.wordSearchStore.indexData.length === 0
                    )}
                  />
                </SearchResults>
              }
            </>
          }
          {
            this.props.word &&
            <>
              <BackButton
                type='button'
                onClick={this.props.onBackToSearchResults}
              >
                <ArrowLeftIcon />
              </BackButton>
              {
                !this.props.showReviewCardsForWordSpinner &&
                this.props.reviewCardsForWord?.length > 0 &&
                <ExistingReviewsWrapper>
                  <ExistingReviewsTitle>
                    {existingReviewsTitle}
                  </ExistingReviewsTitle>
                  {
                    this.props.reviewCardsForWord.map(item => (
                      <ExistingReviewItem key={item.id}>
                        <ReviewCardPanel
                          item={item}
                          embedded
                          contentMarginTop='6px'
                          reviewDeckOptions={this.props.reviewDeckOptions}
                          expanded={this.reviewCardIsExpanded(item.id)}
                          onToggleExpanded={() => this.onToggleReviewCardExpanded(item.id)}
                          showCheckbox={false}
                          onManage={() => this.props.onManageReviewCardForWord(item.id)}
                          onReset={() => this.props.onResetReviewCardForWord(item.id)}
                          onDelete={() => this.props.onDeleteReviewCardForWord(item.id)}
                        />
                      </ExistingReviewItem>
                    ))
                  }
                </ExistingReviewsWrapper>
              }
              <VocabWordInfoList
                dictionaryMeanings
                summaryOnly
                word={this.props.word}
                showPlural={isPresent(this.props.word?.pluralNouns)}
                rowMargin='6px 0'
                margin='2px 0 0 0'
                tablePadding='2px 0 0 0'
              />
            </>
          }
        </FormGroup>
        {
          this.props.word &&
          <>
            <FormGroup>
              <ReviewCreateNote
                showReviewNoteInput={this.props.showReviewNoteInput}
                note={this.props.note}
                onSetNote={this.props.onSetNote}
                onShowReviewNoteInput={this.props.onShowReviewNoteInput}
              />
            </FormGroup>
            <FormGroup
              margin='0 0 20px 0'
            >
              <FieldLabel
                name='review_dictionary_card_type'
                label='Card Type'
              />
              {
                this.props.cardTypeOptions.map(([id, label]) => (
                  <div key={id}>
                    <InPlaceCheckbox
                      value={this.props.cardTypeIds.includes(id)}
                      onChange={() => this.props.onSetCardTypeId(id)}
                    >
                      {label}
                    </InPlaceCheckbox>
                  </div>
                ))
              }
            </FormGroup>
            <FormGroup
              margin='0 0 20px 0'
            >
              <FieldLabel
                name='review_dictionary_deck'
                label='Review Deck'
              />
              <InPlaceSelect
                allowUpdate
                includeBlank
                options={this.props.reviewDeckOptions}
                value={this.props.deckId}
                onChange={val => this.props.onSetDeckId(val)}
              />
            </FormGroup>
          </>
        }
      </Form>
    );
  }
}

export default ReviewCreateWord;
